// src/services/googleSheetsService.js
import { donationApi, contactApi, volunteerApi, giftInKindApi, newsletterApi, visitApi } from './api';

const GOOGLE_SHEETS_URL = process.env.REACT_APP_GOOGLE_SHEETS_URL;

// ============================================
// SAVE TO DATABASE (Primary)
// ============================================
const saveToDatabase = async (databaseFn, data, type) => {
  try {
    const result = await databaseFn();
    console.log(`✅ ${type} saved to Database:`, result);
    return { success: true, source: 'database', data: result };
  } catch (error) {
    console.warn(`⚠️ Database save failed for ${type}:`, error);
    return { success: false, source: 'database', error: error.message };
  }
};

// ============================================
// SAVE TO GOOGLE SHEETS (Backup - Original)
// ============================================
const saveToGoogleSheets = async (sheetsData, endpoint) => {
  try {
    if (!GOOGLE_SHEETS_URL) {
      console.warn('Google Sheets URL not configured');
      return { success: false, error: 'Google Sheets not configured' };
    }

    await fetch(GOOGLE_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint: endpoint,
        payload: sheetsData
      })
    });
    
    console.log(`✅ ${endpoint} saved to Google Sheets`);
    return { success: true, source: 'google-sheets' };
  } catch (error) {
    console.warn(`⚠️ Google Sheets save failed:`, error);
    return { success: false, source: 'google-sheets', error: error.message };
  }
};

// ============================================
// SAVE TO LOCAL STORAGE (Last Resort)
// ============================================
const saveToLocalStorage = (sheetsData, endpoint) => {
  try {
    const pendingKey = `pending_${endpoint}_${Date.now()}`;
    localStorage.setItem(pendingKey, JSON.stringify({
      data: sheetsData,
      endpoint: endpoint,
      failedAt: new Date().toISOString()
    }));
    console.log(`💾 ${endpoint} saved locally as pending`);
    return { success: true, source: 'local-storage' };
  } catch (error) {
    console.error(`❌ Local storage failed:`, error);
    return { success: false, source: 'local-storage', error: error.message };
  }
};

// ============================================
// MAIN: Save to Both Database & Google Sheets
// ============================================
const saveToBoth = async (databaseFn, sheetsData, sheetsEndpoint, type) => {
  const results = {
    database: null,
    sheets: null,
    local: null
  };

  // ✅ 1. Try Database
  results.database = await saveToDatabase(databaseFn, sheetsData, type);

  // ✅ 2. Try Google Sheets (always try, regardless of DB result)
  results.sheets = await saveToGoogleSheets(sheetsData, sheetsEndpoint);

  // ✅ 3. If both failed, save locally
  if (!results.database.success && !results.sheets.success) {
    results.local = await saveToLocalStorage(sheetsData, sheetsEndpoint);
  }

  // Determine overall success
  const overallSuccess = results.database.success || results.sheets.success || results.local?.success || false;
  
  // Build success message
  let message = '';
  if (results.database.success) message += 'Database ✅ ';
  if (results.sheets.success) message += 'Google Sheets ✅ ';
  if (results.local?.success) message += '(Local backup)';

  return {
    success: overallSuccess,
    message: message.trim() || 'All save methods failed',
    sources: {
      database: results.database.success,
      sheets: results.sheets.success,
      local: results.local?.success || false
    },
    data: results.database.data || null
  };
};

// ============================================
// CONTACT FORM
// ============================================
export const submitContact = async (data) => {
  const sheetsData = {
    fullName: data.fullname || data.fullName || '',
    email: data.email || '',
    phone: data.phone || '',
    subject: data.subject || '',
    message: data.message || ''
  };

  return saveToBoth(
    () => contactApi.save({
      fullName: sheetsData.fullName,
      email: sheetsData.email,
      phone: sheetsData.phone,
      subject: sheetsData.subject,
      message: sheetsData.message
    }),
    sheetsData,
    'contact',
    'Contact'
  );
};

// ============================================
// DONATION
// ============================================
export const logDonation = async (data) => {
  const sheetsData = {
    fullName: data.fullName || 'Anonymous',
    email: data.email || '',
    phone: data.phone || '',
    amount: data.amount || '0',
    paymentMethod: data.paymentMethod || 'Unknown',
    reference: data.reference || '',
    status: data.status || 'Pending',
    donationType: data.donationType || 'one-time',
    transactionDate: data.transactionDate || new Date().toISOString()
  };

  return saveToBoth(
    () => donationApi.save({
      fullName: sheetsData.fullName,
      email: sheetsData.email,
      phone: sheetsData.phone,
      amount: parseFloat(sheetsData.amount) || 0,
      paymentMethod: sheetsData.paymentMethod,
      reference: sheetsData.reference,
      status: sheetsData.status,
      donationType: sheetsData.donationType,
      transactionDate: sheetsData.transactionDate
    }),
    sheetsData,
    'donation',
    'Donation'
  );
};

// ============================================
// CONTACT MESSAGE (Alias for submitContact)
// ============================================
export const logContact = submitContact;

// ============================================
// VISIT REQUEST
// ============================================
export const logVisit = async (data) => {
  const sheetsData = {
    organization: data.organization || '',
    registered: data.registered || '',
    registeredName: data.registeredName || '',
    department: data.department || '',
    regNumber: data.regNumber || '',
    teamLeader: data.teamLeader || '',
    teamLeaderId: data.teamLeaderId || '',
    phone: data.phone || '',
    email: data.email || '',
    visitDate: data.visitDate || '',
    people: data.people || '',
    activities: data.activities || '',
    teamMembers: data.teamMembers || ''
  };

  console.log('📤 Sending visit data:', sheetsData);

  return saveToBoth(
    () => visitApi.save({
      organization: sheetsData.organization,
      registered: sheetsData.registered,
      registeredName: sheetsData.registeredName,
      department: sheetsData.department,
      regNumber: sheetsData.regNumber,
      teamLeader: sheetsData.teamLeader,
      teamLeaderId: sheetsData.teamLeaderId,
      phone: sheetsData.phone,
      email: sheetsData.email,
      visitDate: sheetsData.visitDate,
      people: sheetsData.people,
      activities: sheetsData.activities,
      teamMembers: sheetsData.teamMembers
    }),
    sheetsData,
    'visit',
    'Visit Request'
  );
};

// ============================================
// VOLUNTEER APPLICATION
// ============================================
export const logVolunteer = async (data) => {
  const sheetsData = {
    fullName: data.fullName || '',
    email: data.email || '',
    phone: data.phone || '',
    age: data.age || 'Not specified',
    occupation: data.occupation || '',
    skills: data.skills || '',
    availability: data.availability || '',
    interests: data.interests || '',
    message: data.message || '',
    heardFrom: data.heardFrom || ''
  };

  return saveToBoth(
    () => volunteerApi.save({
      fullName: sheetsData.fullName,
      email: sheetsData.email,
      phone: sheetsData.phone,
      age: sheetsData.age,
      occupation: sheetsData.occupation,
      skills: sheetsData.skills,
      availability: sheetsData.availability,
      interests: sheetsData.interests,
      message: sheetsData.message,
      heardFrom: sheetsData.heardFrom
    }),
    sheetsData,
    'volunteer',
    'Volunteer Application'
  );
};

// ============================================
// GIFT IN KIND
// ============================================
export const logGiftInKind = async (data) => {
  const sheetsData = {
    fullName: data.fullName || '',
    email: data.email || '',
    phone: data.phone || '',
    itemType: data.itemType || '',
    itemDescription: data.itemDescription || '',
    quantity: data.quantity || '',
    pickupAvailable: data.pickupAvailable || 'no',
    pickupAddress: data.pickupAddress || '',
    message: data.message || ''
  };

  return saveToBoth(
    () => giftInKindApi.save({
      fullName: sheetsData.fullName,
      email: sheetsData.email,
      phone: sheetsData.phone,
      itemType: sheetsData.itemType,
      itemDescription: sheetsData.itemDescription,
      quantity: sheetsData.quantity,
      pickupAvailable: sheetsData.pickupAvailable,
      pickupAddress: sheetsData.pickupAddress,
      message: sheetsData.message
    }),
    sheetsData,
    'giftinkind',
    'Gift In Kind'
  );
};

// ============================================
// NEWSLETTER SIGNUP
// ============================================
export const logNewsletter = async (data) => {
  const sheetsData = {
    email: data.email || '',
    name: data.name || ''
  };

  return saveToBoth(
    () => newsletterApi.save({
      email: sheetsData.email,
      name: sheetsData.name
    }),
    sheetsData,
    'newsletter',
    'Newsletter Signup'
  );
};

// ============================================
// IPN CALLBACK (Google Sheets Only)
// ============================================
export const logIPNCallback = async (data) => {
  try {
    if (!GOOGLE_SHEETS_URL) {
      console.warn('Google Sheets URL not configured');
      return { success: false, error: 'Google Sheets not configured' };
    }

    await fetch(GOOGLE_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint: 'ipn',
        payload: {
          orderTrackingId: data.orderTrackingId || '',
          merchantReference: data.merchantReference || '',
          status: data.status || '',
          amount: data.amount || '',
          currency: data.currency || 'KES',
          paymentMethod: data.paymentMethod || '',
          confirmationCode: data.confirmationCode || '',
          paymentDate: data.paymentDate || '',
          notificationType: data.notificationType || ''
        }
      })
    });
    
    console.log('✅ IPN callback logged to Google Sheets');
    return { success: true, source: 'google-sheets' };
  } catch (error) {
    console.error('Error logging IPN callback:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// RETRY PENDING DONATIONS
// ============================================
export const retryPendingDonations = async () => {
  try {
    const pendingKeys = Object.keys(localStorage).filter(key => key.startsWith('pending_'));
    let successCount = 0;
    
    for (const key of pendingKeys) {
      try {
        const item = JSON.parse(localStorage.getItem(key));
        const { data, endpoint } = item;
        
        let result;
        switch (endpoint) {
          case 'donation':
            result = await donationApi.save(data);
            break;
          case 'contact':
            result = await contactApi.save(data);
            break;
          case 'volunteer':
            result = await volunteerApi.save(data);
            break;
          case 'giftinkind':
            result = await giftInKindApi.save(data);
            break;
          case 'newsletter':
            result = await newsletterApi.save(data);
            break;
          case 'visit':
            result = await visitApi.save(data);
            break;
          default:
            continue;
        }
        
        if (result.success) {
          localStorage.removeItem(key);
          successCount++;
          console.log(`✅ Synced pending ${endpoint} successfully`);
        }
      } catch (err) {
        console.error(`❌ Failed to sync pending ${key}:`, err);
      }
    }
    
    return { success: true, count: successCount };
  } catch (error) {
    console.error('❌ Error retrying pending donations:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// CHECK PENDING DONATIONS
// ============================================
export const hasPendingDonations = () => {
  return Object.keys(localStorage).some(key => key.startsWith('pending_'));
};

export const getPendingDonations = () => {
  const pending = [];
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('pending_')) {
      try {
        pending.push(JSON.parse(localStorage.getItem(key)));
      } catch (e) {
        // ignore
      }
    }
  });
  return pending;
};

// ============================================
// DEFAULT EXPORT
// ============================================
export default {
  submitContact,
  logDonation,
  logContact,
  logVisit,
  logVolunteer,
  logGiftInKind,
  logNewsletter,
  logIPNCallback,
  retryPendingDonations,
  hasPendingDonations,
  getPendingDonations
};